import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
import moment, { Moment } from "moment";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface PredefinedDateRangesProps {
  onDateChange: (start: Moment, end: Moment) => void;
  startDate?: Moment | null;
  endDate?: Moment | null;
  dateFormat?: string;
}

const PredefinedDateRanges: React.FC<PredefinedDateRangesProps> = ({
  onDateChange,
  startDate,
  endDate,
  dateFormat = "DD/MM/YYYY",
}) => {
  const [dateRange, setDateRange] = useState<[Moment, Moment]>([
    moment(),
    moment(),
  ]);

  useEffect(() => {
    if (startDate && endDate) {
      setDateRange([startDate, endDate]);
    }
  }, [startDate, endDate]);

  // Convert Moment → Dayjs for Ant Design
  const momentToDayjs = (momentDate: Moment) => dayjs(momentDate.toDate());

  // Convert Dayjs → Moment
  const dayjsToMoment = (dayjsDate: any): Moment | null =>
    dayjsDate ? moment(dayjsDate.toDate()) : null;

  const handleDateChange = (
    dates: [any, any] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      const start = dayjsToMoment(dates[0])!.clone().startOf("day"); // 00:00:00
      const end = dayjsToMoment(dates[1])!.clone().endOf("day"); // 23:59:59

      setDateRange([start, end]);
      onDateChange(start, end);
    }
  };

  // Convert value for Ant Design RangePicker
  const getAntdValue = (): [any, any] => {
    return [momentToDayjs(dateRange[0]), momentToDayjs(dateRange[1])];
  };

  // Define predefined ranges (converted to Dayjs)
  const ranges: Record<string, [any, any]> = {
    Today: [momentToDayjs(moment()), momentToDayjs(moment())],
    Yesterday: [
      momentToDayjs(moment().subtract(1, "days")),
      momentToDayjs(moment().subtract(1, "days")),
    ],
    "Last 7 Days": [
      momentToDayjs(moment().subtract(6, "days")),
      momentToDayjs(moment()),
    ],
    "Last 30 Days": [
      momentToDayjs(moment().subtract(29, "days")),
      momentToDayjs(moment()),
    ],
    "This Month": [
      momentToDayjs(moment().startOf("month")),
      momentToDayjs(moment().endOf("month")),
    ],
    "Last Month": [
      momentToDayjs(moment().subtract(1, "month").startOf("month")),
      momentToDayjs(moment().subtract(1, "month").endOf("month")),
    ],
    "This Year": [
      momentToDayjs(moment().startOf("year")),
      momentToDayjs(moment().endOf("year")),
    ],
    "Last Year": [
      momentToDayjs(moment().subtract(1, "year").startOf("year")),
      momentToDayjs(moment().subtract(1, "year").endOf("year")),
    ],
  };

  // Format the display label
  const formatLabel = () => {
    return `${dateRange[0].format(dateFormat)} - ${dateRange[1].format(
      dateFormat
    )}`;
  };

  return (
    <div style={{ display: "inline-block", width: "100%" }}>
      <RangePicker
        onChange={handleDateChange}
        value={getAntdValue()}
        style={{ width: "100%" }}
        ranges={ranges}
        format={dateFormat}
        allowClear={false}
        renderExtraFooter={() => (
          <div
            style={{
              padding: "10px",
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              fontSize: "12px",
              color: "#666",
            }}>
            {formatLabel()}
          </div>
        )}
      />
    </div>
  );
};

export default PredefinedDateRanges;
